import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth, authFetch } from '../../context/AuthContext';
import { StockProduct } from '../../components/ui/ProductSearch';
import MaterialReceiptModal from './MaterialReceiptModal';
import ProductShipmentModal from './ProductShipmentModal';

/* ─── 카테고리 목록 (DB와 동기화) ─── */
const CATEGORIES = ['전체', '내부판매용', '핸드백', '숄더백', '크로스백', '백팩', '여행용'];
const PRODUCT_CATEGORIES = ['내부판매용', '핸드백', '숄더백', '크로스백', '백팩', '여행용 가방', 'OEM/ODM', '기타'];

/* ─── 모달 타입 ─── */
type ModalType = 'add' | 'edit' | 'delete' | 'stock' | null;

/* ─── 공통 인풋 ─── */
const Field = ({ label, required, children }: { label: string; required?: boolean; children: React.ReactNode }) => (
  <div>
    <label className="block text-xs font-semibold text-stone-500 mb-1">{label}{required && ' *'}</label>
    {children}
  </div>
);
const inp = (val: string, onChange: (v: string) => void, props: React.InputHTMLAttributes<HTMLInputElement> = {}) => (
  <input value={val} onChange={e => onChange(e.target.value)} {...props}
    className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" />
);

/* ─── 모달 레이아웃 ─── */
const Modal = ({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) => (
  <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4">
    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg">
      <div className="flex items-center justify-between px-6 py-4 border-b border-stone-100">
        <h2 className="font-bold text-stone-900">{title}</h2>
        <button onClick={onClose} className="text-stone-400 hover:text-stone-700 text-2xl leading-none">×</button>
      </div>
      <div className="px-6 py-5">{children}</div>
    </div>
  </div>
);

/* ─── 초기 폼 ─── */
const blankForm = { code: '', name: '', category: '', price: '', min_stock: '10', description: '' };

export default function InventoryPage() {
  const { user } = useAuth();
  const isAdmin  = user?.role === 'admin';

  const location = useLocation();
  const navigate  = useNavigate();

  const [products,  setProducts]  = useState<StockProduct[]>([]);
  const [loading,   setLoading]   = useState(true);
  const [filter,    setFilter]    = useState('전체');
  const [search,    setSearch]    = useState('');
  const [showForm,  setShowForm]  = useState<'in' | 'out' | null>(null);
  const [toast,     setToast]     = useState('');
  const [onlyLow,   setOnlyLow]   = useState(false);

  /* 관리자 모달 */
  const [modal,     setModal]     = useState<ModalType>(null);
  const [target,    setTarget]    = useState<StockProduct | null>(null);
  const [form,      setForm]      = useState(blankForm);
  const [stockVal,  setStockVal]  = useState('');
  const [stockReason, setStockReason] = useState('');
  const [saving,    setSaving]    = useState(false);
  const [errMsg,    setErrMsg]    = useState('');

  const flash = (msg: string) => { setToast(msg); setTimeout(() => setToast(''), 4000); };

  /* ── 재고 로드 ── */
  const loadInventory = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authFetch('/api/inventory');
      if (res.ok) setProducts(await res.json());
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadInventory(); }, [loadInventory]);

  /* ── URL 파라미터 ── */
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('action') === 'in')  { setShowForm('in');  navigate('/admin/inventory', { replace: true }); }
    if (params.get('action') === 'out') { setShowForm('out'); navigate('/admin/inventory', { replace: true }); }
    if (params.get('filter') === 'low') { setOnlyLow(true);  navigate('/admin/inventory', { replace: true }); }
  }, [location.search, navigate]);

  const handleSuccess = (msg: string) => { setShowForm(null); flash(msg); loadInventory(); };

  /* ── 관리자 모달 오픈 ── */
  const openAdd = () => {
    setForm(blankForm); setErrMsg(''); setModal('add');
  };
  const openEdit = (p: StockProduct) => {
    setTarget(p);
    setForm({ code: p.code, name: p.name, category: p.category, price: String(p.price), min_stock: String(p.min_stock), description: '' });
    setErrMsg(''); setModal('edit');
  };
  const openDelete = (p: StockProduct) => { setTarget(p); setErrMsg(''); setModal('delete'); };
  const openStock  = (p: StockProduct) => { setTarget(p); setStockVal(String(p.stock)); setStockReason(''); setErrMsg(''); setModal('stock'); };
  const closeModal = () => { setModal(null); setTarget(null); setErrMsg(''); };

  /* ── 제품 추가 ── */
  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true); setErrMsg('');
    const res  = await authFetch('/api/products', {
      method: 'POST',
      body: JSON.stringify({ ...form, price: Number(form.price), min_stock: Number(form.min_stock) }),
    });
    const data = await res.json();
    setSaving(false);
    if (!res.ok) { setErrMsg(data.error ?? '추가 실패'); return; }
    await loadInventory();
    flash(`${data.name} 제품이 추가되었습니다.`);
    closeModal();
  };

  /* ── 제품 수정 ── */
  const handleEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!target) return;
    setSaving(true); setErrMsg('');
    const res  = await authFetch(`/api/products/${target.id}`, {
      method: 'PUT',
      body: JSON.stringify({ ...form, price: Number(form.price), min_stock: Number(form.min_stock) }),
    });
    const data = await res.json();
    setSaving(false);
    if (!res.ok) { setErrMsg(data.error ?? '수정 실패'); return; }
    await loadInventory();
    flash('제품 정보가 수정되었습니다.');
    closeModal();
  };

  /* ── 제품 삭제 ── */
  const handleDelete = async () => {
    if (!target) return;
    setSaving(true);
    const res  = await authFetch(`/api/products/${target.id}`, { method: 'DELETE' });
    const data = await res.json();
    setSaving(false);
    await loadInventory();
    flash(data.note ?? '처리 완료');
    closeModal();
  };

  /* ── 재고 수량 직접 조정 ── */
  const handleStockAdjust = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!target) return;
    const qty = parseInt(stockVal);
    if (isNaN(qty) || qty < 0) { setErrMsg('0 이상의 숫자를 입력해주세요.'); return; }
    setSaving(true); setErrMsg('');
    const res  = await authFetch(`/api/products/${target.id}/stock`, {
      method: 'PATCH',
      body: JSON.stringify({ quantity: qty, reason: stockReason || '관리자 재고 조정' }),
    });
    const data = await res.json();
    setSaving(false);
    if (!res.ok) { setErrMsg(data.error ?? '조정 실패'); return; }
    await loadInventory();
    flash(`${target.name} 재고가 ${qty}개로 조정되었습니다.`);
    closeModal();
  };

  /* ── 필터링 ── */
  const filtered = products.filter(p => {
    const catOk    = filter === '전체' || p.category === filter;
    const searchOk = p.name.toLowerCase().includes(search.toLowerCase())
                  || p.code.toLowerCase().includes(search.toLowerCase());
    const lowOk    = onlyLow ? p.is_low === 1 : true;
    return catOk && searchOk && lowOk;
  });

  const lowCount = products.filter(p => p.is_low === 1).length;

  return (
    <div className="p-6 max-w-7xl mx-auto">

      {toast && (
        <div className="mb-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-xl text-sm flex items-center gap-2">
          {toast}
        </div>
      )}

      {/* 헤더 */}
      <div className="flex flex-wrap justify-between items-center mb-6 gap-3">
        <div>
          <h1 className="text-2xl font-bold text-stone-900">재고 관리</h1>
          <p className="text-stone-500 text-sm mt-1">
            자재 입고 및 상품 출고 처리&nbsp;·&nbsp;
            <button onClick={loadInventory} className="text-amber-600 hover:underline text-xs">🔄 새로고침</button>
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {isAdmin && (
            <button onClick={openAdd}
              className="bg-amber-500 hover:bg-amber-600 text-black font-bold px-4 py-2 rounded-lg text-sm transition">
              + 제품 추가
            </button>
          )}
          <button onClick={() => setShowForm('in')}
            className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition">
            자재 입고
          </button>
          <button onClick={() => setShowForm('out')}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition">
            상품 출고
          </button>
        </div>
      </div>

      {/* 재고 부족 알림 */}
      {lowCount > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6 flex items-start justify-between gap-3">
          <div className="flex items-start gap-3">
            <span className="text-red-500 text-xl flex-shrink-0">오류</span>
            <div>
              <p className="font-semibold text-red-700">재고 부족 알림 ({lowCount}개 품목)</p>
              <p className="text-red-600 text-sm mt-0.5">
                {products.filter(p => p.is_low === 1).map(p => p.name).join(', ')}
              </p>
            </div>
          </div>
          <button onClick={() => setOnlyLow(v => !v)}
            className={`text-xs font-semibold px-3 py-1.5 rounded-lg flex-shrink-0 transition ${
              onlyLow ? 'bg-red-600 text-white' : 'bg-red-100 text-red-700 hover:bg-red-200'}`}>
            {onlyLow ? '전체 보기' : '부족만 보기'}
          </button>
        </div>
      )}

      {/* 검색 & 필터 */}
      <div className="bg-white rounded-xl shadow-sm p-4 mb-4 flex flex-wrap gap-3 items-center">
        <input type="text" placeholder="제품명 또는 코드 검색..."
          value={search} onChange={e => setSearch(e.target.value)}
          className="border border-stone-200 rounded-lg px-3 py-2 text-sm flex-1 min-w-48 focus:outline-none focus:ring-2 focus:ring-amber-400" />
        <div className="flex flex-wrap gap-2">
          {CATEGORIES.map(cat => (
            <button key={cat} onClick={() => { setFilter(cat); setOnlyLow(false); }}
              className={`px-4 py-1.5 rounded-full text-sm font-medium transition ${
                filter === cat && !onlyLow ? 'bg-amber-500 text-white' : 'bg-stone-100 text-stone-600 hover:bg-stone-200'}`}>
              {cat}
            </button>
          ))}
        </div>
      </div>

      {/* 재고 테이블 */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        {loading ? (
          <div className="text-center py-16 text-stone-400 animate-pulse">재고 데이터 불러오는 중...</div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="bg-stone-50 border-b border-stone-100">
                <th className="text-left px-4 py-3 text-xs font-semibold text-stone-500">제품 코드</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-stone-500">제품명</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-stone-500">카테고리</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-stone-500">단가</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-stone-500">현재 재고</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-stone-500">최소 재고</th>
                <th className="text-center px-4 py-3 text-xs font-semibold text-stone-500">상태</th>
                {isAdmin && <th className="text-center px-4 py-3 text-xs font-semibold text-stone-500">관리</th>}
              </tr>
            </thead>
            <tbody>
              {filtered.map(p => {
                const isLow = p.is_low === 1;
                return (
                  <tr key={p.id} className="border-b border-stone-50 hover:bg-stone-50 transition">
                    <td className="px-4 py-3 text-xs font-mono text-stone-500">{p.code}</td>
                    <td className="px-4 py-3 text-sm font-medium text-stone-800">{p.name}</td>
                    <td className="px-4 py-3 text-sm text-stone-500">{p.category}</td>
                    <td className="px-4 py-3 text-sm text-right text-stone-700">{p.price.toLocaleString()}원</td>
                    <td className={`px-4 py-3 text-sm text-right font-bold ${isLow ? 'text-red-600' : 'text-stone-800'}`}>
                      {p.stock}
                    </td>
                    <td className="px-4 py-3 text-sm text-right text-stone-400">{p.min_stock}</td>
                    <td className="px-4 py-3 text-center">
                      {isLow
                        ? <span className="bg-red-100 text-red-700 text-xs font-medium px-2 py-0.5 rounded-full">부족</span>
                        : <span className="bg-green-100 text-green-700 text-xs font-medium px-2 py-0.5 rounded-full">정상</span>}
                    </td>
                    {isAdmin && (
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-center gap-1">
                          {/* 재고 수량 조정 */}
                          <button onClick={() => openStock(p)} title="재고 수량 조정"
                            className="p-1.5 rounded hover:bg-amber-50 text-stone-400 hover:text-amber-600 transition">
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14"/>
                            </svg>
                          </button>
                          {/* 정보 수정 */}
                          <button onClick={() => openEdit(p)} title="제품 정보 수정"
                            className="p-1.5 rounded hover:bg-blue-50 text-stone-400 hover:text-blue-600 transition">
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>
                            </svg>
                          </button>
                          {/* 삭제 */}
                          <button onClick={() => openDelete(p)} title="제품 삭제"
                            className="p-1.5 rounded hover:bg-red-50 text-stone-400 hover:text-red-500 transition">
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                            </svg>
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })}
              {filtered.length === 0 && !loading && (
                <tr>
                  <td colSpan={isAdmin ? 8 : 7} className="text-center py-16 text-stone-400">
                    {search || onlyLow ? '검색 결과가 없습니다.' : '등록된 제품이 없습니다.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* 요약 */}
      {!loading && products.length > 0 && (
        <div className="mt-3 flex gap-4 text-xs text-stone-500 px-1">
          <span>전체 {products.length}종</span>
          <span>정상 {products.filter(p => p.is_low === 0).length}종</span>
          <span className="text-red-500 font-semibold">부족 {lowCount}종</span>
          <span>총 재고 {products.reduce((s, p) => s + p.stock, 0).toLocaleString()}개</span>
        </div>
      )}

      {/* ─── 자재 입고 모달 ─── */}
      {showForm === 'in' && (
        <MaterialReceiptModal onClose={() => setShowForm(null)} onSuccess={handleSuccess} />
      )}

      {/* ─── 상품 출고 모달 ─── */}
      {showForm === 'out' && (
        <ProductShipmentModal onClose={() => setShowForm(null)} onSuccess={handleSuccess} />
      )}

      {/* ─── 제품 추가 모달 ─── */}
      {modal === 'add' && (
        <Modal title="제품 추가" onClose={closeModal}>
          <form onSubmit={handleAdd} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <Field label="제품 코드 *">
                {inp(form.code, v => setForm({...form, code: v}), { placeholder: 'BAG-001', required: true })}
              </Field>
              <Field label="카테고리 *">
                <select value={form.category} onChange={e => setForm({...form, category: e.target.value})} required
                  className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400">
                  <option value="">-- 선택 --</option>
                  {PRODUCT_CATEGORIES.map(c => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </Field>
              <Field label="제품명 *" >
                {inp(form.name, v => setForm({...form, name: v}), { placeholder: '클래식 토트백 (블랙)', required: true, className: 'w-full border border-stone-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 col-span-2' })}
              </Field>
              <Field label="단가 (원) *">
                {inp(form.price, v => setForm({...form, price: v}), { type: 'number', placeholder: '185000', required: true, min: '0' })}
              </Field>
              <Field label="최소 재고">
                {inp(form.min_stock, v => setForm({...form, min_stock: v}), { type: 'number', placeholder: '10', min: '0' })}
              </Field>
            </div>
            {errMsg && <p className="text-red-500 text-xs bg-red-50 px-3 py-2 rounded-lg">{errMsg}</p>}
            <div className="flex gap-3 pt-1">
              <button type="button" onClick={closeModal} className="flex-1 border border-stone-200 py-2.5 rounded-lg text-sm hover:bg-stone-50">취소</button>
              <button type="submit" disabled={saving} className="flex-1 bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-black font-bold py-2.5 rounded-lg text-sm">
                {saving ? '저장 중...' : '추가'}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* ─── 제품 수정 모달 ─── */}
      {modal === 'edit' && target && (
        <Modal title={`제품 수정 — ${target.name}`} onClose={closeModal}>
          <form onSubmit={handleEdit} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <Field label="제품 코드">
                {inp(form.code, v => setForm({...form, code: v}), { placeholder: 'BAG-001' })}
              </Field>
              <Field label="카테고리">
                <select value={form.category} onChange={e => setForm({...form, category: e.target.value})}
                  className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400">
                  <option value="">-- 선택 --</option>
                  {PRODUCT_CATEGORIES.map(c => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </Field>
            </div>
            <Field label="제품명">
              {inp(form.name, v => setForm({...form, name: v}), { placeholder: '제품명' })}
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="단가 (원)">
                {inp(form.price, v => setForm({...form, price: v}), { type: 'number', min: '0' })}
              </Field>
              <Field label="최소 재고">
                {inp(form.min_stock, v => setForm({...form, min_stock: v}), { type: 'number', min: '0' })}
              </Field>
            </div>
            {errMsg && <p className="text-red-500 text-xs bg-red-50 px-3 py-2 rounded-lg">{errMsg}</p>}
            <div className="flex gap-3 pt-1">
              <button type="button" onClick={closeModal} className="flex-1 border border-stone-200 py-2.5 rounded-lg text-sm hover:bg-stone-50">취소</button>
              <button type="submit" disabled={saving} className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-bold py-2.5 rounded-lg text-sm">
                {saving ? '저장 중...' : '저장'}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* ─── 재고 수량 조정 모달 ─── */}
      {modal === 'stock' && target && (
        <Modal title={`재고 수량 조정 — ${target.name}`} onClose={closeModal}>
          <form onSubmit={handleStockAdjust} className="space-y-4">
            <div className="bg-stone-50 rounded-xl p-4 flex justify-between text-sm">
              <span className="text-stone-500">현재 재고</span>
              <span className="font-bold text-stone-800">{target.stock}개</span>
            </div>
            <Field label="조정 후 재고 수량 *">
              <input type="number" value={stockVal} onChange={e => setStockVal(e.target.value)}
                min="0" required autoFocus
                className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
                placeholder="조정할 수량 입력" />
            </Field>
            {stockVal !== '' && !isNaN(parseInt(stockVal)) && (
              <div className={`text-sm px-3 py-2 rounded-lg ${
                parseInt(stockVal) > target.stock ? 'bg-green-50 text-green-700'
                : parseInt(stockVal) < target.stock ? 'bg-red-50 text-red-700'
                : 'bg-stone-50 text-stone-500'}`}>
                {parseInt(stockVal) > target.stock
                  ? `▲ ${parseInt(stockVal) - target.stock}개 증가`
                  : parseInt(stockVal) < target.stock
                  ? `▼ ${target.stock - parseInt(stockVal)}개 감소`
                  : '변경 없음'}
              </div>
            )}
            <Field label="조정 사유">
              <input type="text" value={stockReason} onChange={e => setStockReason(e.target.value)}
                className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
                placeholder="예: 실사 조정, 파손 폐기 등" />
            </Field>
            {errMsg && <p className="text-red-500 text-xs bg-red-50 px-3 py-2 rounded-lg">{errMsg}</p>}
            <div className="flex gap-3 pt-1">
              <button type="button" onClick={closeModal} className="flex-1 border border-stone-200 py-2.5 rounded-lg text-sm hover:bg-stone-50">취소</button>
              <button type="submit" disabled={saving} className="flex-1 bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-black font-bold py-2.5 rounded-lg text-sm">
                {saving ? '적용 중...' : '재고 조정'}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* ─── 삭제 확인 모달 ─── */}
      {modal === 'delete' && target && (
        <Modal title="제품 삭제" onClose={closeModal}>
          <div className="text-center py-2">
            <div className="text-5xl mb-4">오류</div>
            <p className="font-semibold text-stone-800 mb-1">{target.name}</p>
            <p className="text-stone-500 text-sm mb-6">
              입출고·생산 이력이 있으면 비활성화로 처리됩니다.
            </p>
            {errMsg && <p className="text-red-500 text-xs mb-3">{errMsg}</p>}
            <div className="flex gap-3">
              <button onClick={closeModal} className="flex-1 border border-stone-200 py-2.5 rounded-lg text-sm hover:bg-stone-50">취소</button>
              <button onClick={handleDelete} disabled={saving}
                className="flex-1 bg-red-500 hover:bg-red-600 disabled:opacity-50 text-white font-bold py-2.5 rounded-lg text-sm">
                {saving ? '처리 중...' : '삭제'}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
